/**
 * WithVerticalSources Component
 * Wraps chat content and displays a vertical sources panel on the right side
 * Similar to online-community's WithVerticalSources
 */

import React, { ReactNode } from 'react';

import { useTranscriptContext } from '../../../../store/transcript/utils/useTranscriptContext';
import { MessageReference } from '../../../Message/types/messages';
import AskHumanBox from '../AskHuman/AskHumanBox';
import SourceListVertical from './SourceListVertical';
import { ChatPanel, ChatWrapper } from './WithVerticalSources.styled';

export interface WithVerticalSourcesProps {
  children: ReactNode;
  className?: string;
  shouldDisplayAskHumanBox?: boolean;
  askHumanButtonText?: string;
  askHumanPromptText?: string;
  showVerticalSources?: boolean;
}

/**
 * Gets the most recent AI assistant message sources
 */
const useMostRecentAiSources = (): MessageReference[] => {
  const { state } = useTranscriptContext();
  
  if (!state.messages || state.messages.length === 0) {
    return [];
  }

  // Find the most recent assistant message with sources
  for (let i = state.messages.length - 1; i >= 0; i--) {
    const message = state.messages[i];
    if (message.role === 'assistant' && message.messageReferences && message.messageReferences.length > 0) {
      return message.messageReferences;
    }
  }

  return [];
};

/**
 * WithVerticalSources Component
 * Creates a split layout with chat on the left and sources panel on the right
 */
export const WithVerticalSources: React.FC<WithVerticalSourcesProps> = ({
  children,
  className = '',
  shouldDisplayAskHumanBox = true,
  askHumanButtonText = 'Ask your fellow humans',
  askHumanPromptText = 'Need opinions? AI can help you post.',
  showVerticalSources = true,
}) => {
  const mostRecentSources = useMostRecentAiSources();
  const { state } = useTranscriptContext();
  const hasMessages = state.messages && state.messages.length > 0;
  
  // Initial state is when we only have the welcome message (1 message)
  const isInitialState = !hasMessages || state.messages.length <= 1;
  
  // Show sources if:
  // - Feature is enabled (showVerticalSources)
  // - We have an active session with messages beyond the welcome
  // - There are sources to display
  const shouldShowSources = showVerticalSources && hasMessages && mostRecentSources.length > 0;

  return (
    <ChatPanel className={className}>
      <ChatWrapper $isInitialState={isInitialState}>{children}</ChatWrapper>
      {shouldShowSources && (
        <SourceListVertical
          sources={mostRecentSources}
          footer={
            shouldDisplayAskHumanBox ? (
              <AskHumanBox
                promptText={askHumanPromptText}
                buttonText={askHumanButtonText}
              />
            ) : undefined
          }
        />
      )}
    </ChatPanel>
  );
};

export default WithVerticalSources;

